import React, { FC } from 'react';
import styled from 'styled-components';
import { formatPostalCode } from 'utils/formatters/formatPostalCode';

import { IconEnum } from 'components/common/Icon';
import InputContainer from 'components/common/InputContainer';
import TextInput from 'components/common/Inputs/TextInput';

const Form = styled.form`
    display: flex;
    flex-direction: column;
    padding: 25px;
    gap: 30px;
    width: 100%;
        
    .icon {
        ${(props) => props.darkMode ? 'filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(79deg) brightness(105%) contrast(102%);' : ''};
    }
`;

const FormRow = styled.div`
    display: flex;
    gap: 20px;
    
    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const FormColumn = styled.div`
    flex: 1;
`;

const SectionTitle = styled.h2`
    font-size: 22px;
    margin-bottom: 20px;
    color: var(--color-primary);
`;

export interface AccountFormData {
    email: string;
    password: string;
    confirmPassword: string;
}

export interface InvoiceFormData {
    firstName: string;
    lastName: string;
    companyName: string;
    taxId: string;
    phoneNumber: string;
    street: string;
    city: string;
    postalCode: string;
}

export interface FormErrors {
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    companyName: string;
    taxId: string;
    phoneNumber: string;
    street: string;
    city: string;
    postalCode: string;
}

interface ProductPurchaseFormProps {
    isCreateAccount: boolean;
    isDarkMode: boolean;
    accountData?: AccountFormData;
    invoiceData: InvoiceFormData;
    errors: FormErrors;
    triggerValidation: boolean;
    onAccountDataChange?: (field: keyof AccountFormData, value: string) => void;
    onInvoiceDataChange: (field: keyof InvoiceFormData, value: string) => void;
    onSubmit: (e: React.FormEvent) => void;
    children?: React.ReactNode;
}

export const initialAccountState: AccountFormData = {
    email: '',
    password: '',
    confirmPassword: ''
};

export const initialInvoiceState: InvoiceFormData = {
    firstName: '',
    lastName: '',
    companyName: '',
    taxId: '',
    phoneNumber: '',
    street: '',
    city: '',
    postalCode: ''
};

export const initialErrorState: FormErrors = {
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    taxId: '',
    phoneNumber: '',
    street: '',
    city: '',
    postalCode: ''
};

export const validateForm = (accountData: AccountFormData | undefined, invoiceData: InvoiceFormData, isCreateAccount: boolean): FormErrors => {
    const newErrors: FormErrors = { ...initialErrorState };

    // Walidacja danych dla CreateAccount
    if (isCreateAccount && accountData) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!accountData.email) {
            newErrors.email = 'Email jest wymagany';
        } else if (!emailRegex.test(accountData.email)) {
            newErrors.email = 'Podaj poprawny adres email';
        }

        if (!accountData.password) {
            newErrors.password = 'Hasło jest wymagane';
        } else if (accountData.password.length < 8) {
            newErrors.password = 'Hasło musi mieć minimum 8 znaków';
        } else if (!(/[A-Z]/).test(accountData.password)) {
            newErrors.password = 'Hasło musi zawierać co najmniej jedną dużą literę';
        } else if (!(/\d/).test(accountData.password)) {
            newErrors.password = 'Hasło musi zawierać co najmniej jedną cyfrę';
        }

        if (accountData.password !== accountData.confirmPassword) {
            newErrors.confirmPassword = 'Hasła nie są identyczne';
        }
    }

    // Walidacja danych do faktury
    if (!invoiceData.firstName) newErrors.firstName = 'Imię jest wymagane';
    if (!invoiceData.lastName) newErrors.lastName = 'Nazwisko jest wymagane';
    if (!invoiceData.phoneNumber) newErrors.phoneNumber = 'Numer telefonu jest wymagany';
    if (!invoiceData.street) newErrors.street = 'Adres jest wymagany';
    if (!invoiceData.city) newErrors.city = 'Miasto jest wymagane';
    if (!invoiceData.postalCode) newErrors.postalCode = 'Kod pocztowy jest wymagany';

    if (invoiceData.companyName && !invoiceData.taxId) {
        newErrors.taxId = 'NIP jest wymagany dla firmy';
    } else if (invoiceData.taxId && !(/^\d{10}$/).test(invoiceData.taxId)) {
        newErrors.taxId = 'NIP musi składać się z 10 cyfr';
    }

    if (invoiceData.postalCode && !(/^\d{2}-\d{3}$/).test(invoiceData.postalCode)) {
        newErrors.postalCode = 'Kod pocztowy musi być w formacie XX-XXX';
    }

    return newErrors;
};

export const hasErrors = (errors: FormErrors): boolean => {
    return Object.values(errors).some((error) => error !== '');
};

const ProductPurchaseForm: FC<ProductPurchaseFormProps> = ({
    isCreateAccount,
    isDarkMode,
    accountData,
    invoiceData,
    errors,
    triggerValidation,
    onAccountDataChange,
    onInvoiceDataChange,
    onSubmit,
    children
}) => {
    const handleInvoiceChange = (field: keyof InvoiceFormData, value: string) => {
        let processedValue = value;

        if (field === 'postalCode') {
            processedValue = formatPostalCode(value);
        } else if (field === 'taxId') {
            processedValue = value.replace(/\D/g, '');
        }

        onInvoiceDataChange(field, processedValue);
    };

    const handleAccountChange = (field: keyof AccountFormData, value: string) => {
        if (onAccountDataChange) {
            onAccountDataChange(field, value);
        }
    };

    return (
        <Form darkMode={isDarkMode} onSubmit={onSubmit}>
            {/* Sekcja danych konta dla CreateAccount */}
            {isCreateAccount && accountData &&
                <>
                    <SectionTitle>Dane konta</SectionTitle>

                    <InputContainer title={'Adres email'} icon={IconEnum.EMAIL} required>
                        <TextInput
                            type={'email'}
                            placeholder={'Podaj adres email...'}
                            onChange={({ target: { value } }) => handleAccountChange('email', value)}
                            value={accountData.email}
                            required
                            errorMessage={errors.email}
                            inputValidation={() => !!errors.email}
                            triggerValidation={triggerValidation}
                            errorMessageBigger
                        />
                    </InputContainer>

                    <FormRow>
                        <FormColumn>
                            <InputContainer title={'Hasło'} icon={IconEnum.BLOCKED} required>
                                <TextInput
                                    type={'password'}
                                    placeholder={'Min. 8 znaków, duża litera i cyfra'}
                                    onChange={({ target: { value } }) => handleAccountChange('password', value)}
                                    value={accountData.password}
                                    required
                                    errorMessage={errors.password}
                                    inputValidation={() => !!errors.password}
                                    triggerValidation={triggerValidation}
                                    errorMessageBigger
                                />
                            </InputContainer>
                        </FormColumn>
                        <FormColumn>
                            <InputContainer title={'Potwierdź hasło'} icon={IconEnum.BLOCKED} required>
                                <TextInput
                                    type={'password'}
                                    placeholder={'Potwierdź hasło...'}
                                    onChange={({ target: { value } }) => handleAccountChange('confirmPassword', value)}
                                    value={accountData.confirmPassword}
                                    required
                                    errorMessage={errors.confirmPassword}
                                    inputValidation={() => !!errors.confirmPassword}
                                    triggerValidation={triggerValidation}
                                    errorMessageBigger
                                />
                            </InputContainer>
                        </FormColumn>
                    </FormRow>
                </>
            }

            {/* Sekcja danych do faktury */}
            <SectionTitle>{isCreateAccount ? 'Dane do faktury' : 'Dane do zamówienia'}</SectionTitle>

            <FormRow>
                <FormColumn>
                    <InputContainer title={'Imię'} icon={IconEnum.PEOPLE} required>
                        <TextInput
                            type={'text'}
                            placeholder={'Wpisz imię...'}
                            onChange={({ target: { value } }) => handleInvoiceChange('firstName', value)}
                            value={invoiceData.firstName}
                            required
                            errorMessage={errors.firstName}
                            inputValidation={() => !!errors.firstName}
                            triggerValidation={triggerValidation}
                            errorMessageBigger
                        />
                    </InputContainer>
                </FormColumn>
                <FormColumn>
                    <InputContainer title={'Nazwisko'} icon={IconEnum.PEOPLE} required>
                        <TextInput
                            type={'text'}
                            placeholder={'Wpisz nazwisko...'}
                            onChange={({ target: { value } }) => handleInvoiceChange('lastName', value)}
                            value={invoiceData.lastName}
                            required
                            errorMessage={errors.lastName}
                            inputValidation={() => !!errors.lastName}
                            triggerValidation={triggerValidation}
                            errorMessageBigger
                        />
                    </InputContainer>
                </FormColumn>
            </FormRow>

            <FormRow>
                <FormColumn>
                    <InputContainer title={'Nazwa firmy'} icon={IconEnum.BUILDING} helperText={isCreateAccount ? 'Opcjonalne' : 'Opcjonalne'}>
                        <TextInput
                            type={'text'}
                            placeholder={isCreateAccount ? 'Opcjonalnie - nazwa firmy...' : 'Wpisz nazwę firmy...'}
                            onChange={({ target: { value } }) => handleInvoiceChange('companyName', value)}
                            value={invoiceData.companyName}
                            errorMessage={errors.companyName}
                            inputValidation={() => !!errors.companyName}
                            triggerValidation={triggerValidation}
                            errorMessageBigger
                        />
                    </InputContainer>
                </FormColumn>
                <FormColumn>
                    <InputContainer title={'NIP'} icon={IconEnum.SQUARE} helperText={'Wymagane dla firm'}>
                        <TextInput
                            type={'text'}
                            placeholder={isCreateAccount ? 'Wymagane dla firmy...' : 'Wpisz NIP...'}
                            onChange={({ target: { value } }) => handleInvoiceChange('taxId', value)}
                            value={invoiceData.taxId}
                            maxLength={10}
                            errorMessage={errors.taxId}
                            inputValidation={() => !!errors.taxId}
                            triggerValidation={triggerValidation}
                            errorMessageBigger
                        />
                    </InputContainer>
                </FormColumn>
            </FormRow>

            <FormRow>
                <FormColumn>
                    <InputContainer title={isCreateAccount ? 'Adres' : 'Ulica i numer'} icon={IconEnum.MAP_PIN} required>
                        <TextInput
                            type={'text'}
                            placeholder={isCreateAccount ? 'Ulica i numer domu/mieszkania...' : 'Wpisz ulicę i numer...'}
                            onChange={({ target: { value } }) => handleInvoiceChange('street', value)}
                            value={invoiceData.street}
                            required
                            errorMessage={errors.street}
                            inputValidation={() => !!errors.street}
                            triggerValidation={triggerValidation}
                            errorMessageBigger
                        />
                    </InputContainer>
                </FormColumn>
                <FormColumn>
                    <InputContainer title={'Miasto'} icon={IconEnum.MAP_PIN} required>
                        <TextInput
                            type={'text'}
                            placeholder={'Wpisz miasto...'}
                            onChange={({ target: { value } }) => handleInvoiceChange('city', value)}
                            value={invoiceData.city}
                            required
                            errorMessage={errors.city}
                            inputValidation={() => !!errors.city}
                            triggerValidation={triggerValidation}
                            errorMessageBigger
                        />
                    </InputContainer>
                </FormColumn>
            </FormRow>

            <FormRow>
                <FormColumn>
                    <InputContainer title={'Kod pocztowy'} icon={IconEnum.MAP_PIN} required>
                        <TextInput
                            type={'text'}
                            placeholder={'XX-XXX'}
                            onChange={({ target: { value } }) => handleInvoiceChange('postalCode', value)}
                            value={invoiceData.postalCode}
                            required
                            maxLength={6}
                            errorMessage={errors.postalCode}
                            inputValidation={() => !!errors.postalCode}
                            triggerValidation={triggerValidation}
                            errorMessageBigger
                        />
                    </InputContainer>
                </FormColumn>
                <FormColumn>
                    <InputContainer title={'Numer telefonu'} icon={IconEnum.PHONE} required>
                        <TextInput
                            type={'tel'}
                            placeholder={'Wpisz numer telefonu...'}
                            onChange={({ target: { value } }) => handleInvoiceChange('phoneNumber', value)}
                            value={invoiceData.phoneNumber}
                            required
                            errorMessage={errors.phoneNumber}
                            inputValidation={() => !!errors.phoneNumber}
                            triggerValidation={triggerValidation}
                            errorMessageBigger
                        />
                    </InputContainer>
                </FormColumn>
            </FormRow>

            {children}
        </Form>
    );
};

export default ProductPurchaseForm;

